import type { HandbookTrainingNode } from '../types/handbookTraining';

export type HandbookTrainingTreeNode = HandbookTrainingNode & {
  children: HandbookTrainingTreeNode[];
  depth: number;
};

function parentKey(parentId: string | null | undefined): string {
  return parentId || '__root__';
}

function resolveParentId(node: HandbookTrainingNode, byId: Map<string, HandbookTrainingNode>): string | null {
  const pid = node.parentId;
  if (!pid) return null;
  const parent = byId.get(pid);
  if (!parent || parent.kind !== 'folder') return null;
  return pid;
}

export function buildHandbookTrainingTree(
  nodes: HandbookTrainingNode[],
  options?: { publishedOnly?: boolean },
): HandbookTrainingTreeNode[] {
  const publishedOnly = options?.publishedOnly === true;
  const filtered = publishedOnly ? nodes.filter(n => n.published || n.kind === 'folder') : nodes;

  const byId = new Map(filtered.map(n => [n.id, n]));

  const byParent = new Map<string, HandbookTrainingNode[]>();
  for (const n of filtered) {
    const key = parentKey(resolveParentId(n, byId));
    const list = byParent.get(key) || [];
    list.push(n);
    byParent.set(key, list);
  }
  Array.from(byParent.values()).forEach(list => {
    list.sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
  });

  function build(parentId: string | null, depth: number): HandbookTrainingTreeNode[] {
    const key = parentKey(parentId);
    const siblings = byParent.get(key) || [];
    return siblings
      .filter(n => {
        if (!publishedOnly) return true;
        if (n.kind === 'video') return n.published;
        return hasPublishedDescendant(n.id, byParent, publishedOnly);
      })
      .map(n => ({
        ...n,
        depth,
        children: n.kind === 'folder' ? build(n.id, depth + 1) : [],
      }));
  }

  return build(null, 0);
}

function hasPublishedDescendant(
  folderId: string,
  byParent: Map<string, HandbookTrainingNode[]>,
  publishedOnly: boolean,
): boolean {
  const children = byParent.get(folderId) || [];
  for (const c of children) {
    if (c.kind === 'video' && c.published) return true;
    if (c.kind === 'folder' && hasPublishedDescendant(c.id, byParent, publishedOnly)) return true;
  }
  return !publishedOnly;
}

/** Folder titles from root → parent (excludes the node itself). */
export function handbookTrainingBreadcrumbTitles(
  nodes: HandbookTrainingNode[],
  parentId: string | null,
): string[] {
  const byId = new Map(nodes.map(n => [n.id, n]));
  const titles: string[] = [];
  let pid = parentId;
  while (pid) {
    const p = byId.get(pid);
    if (!p) break;
    titles.unshift(p.title);
    pid = p.parentId;
  }
  return titles;
}

export function handbookTrainingAncestorIds(
  nodes: HandbookTrainingNode[],
  nodeId: string,
): string[] {
  const byId = new Map(nodes.map(n => [n.id, n]));
  const out: string[] = [];
  let cur = byId.get(nodeId);
  while (cur?.parentId) {
    out.unshift(cur.parentId);
    cur = byId.get(cur.parentId);
  }
  return out;
}

export function handbookTrainingSearchMatch(node: HandbookTrainingNode, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const blob = [
    node.title,
    node.description,
    node.slug,
    ...(node.covers?.routes || []),
    ...(node.covers?.features || []),
    ...(node.covers?.apis || []),
    ...(node.staleIf || []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return blob.includes(q);
}

export function folderDepthLabel(depth: number): string {
  if (depth === 0) return 'Section';
  if (depth === 1) return 'Topic';
  if (depth === 2) return 'Subtopic';
  return 'Folder';
}

export function findHandbookTrainingTreeNode(
  tree: HandbookTrainingTreeNode[],
  nodeId: string,
): HandbookTrainingTreeNode | undefined {
  for (const n of tree) {
    if (n.id === nodeId) return n;
    const nested = findHandbookTrainingTreeNode(n.children, nodeId);
    if (nested) return nested;
  }
  return undefined;
}

/** True if `maybeDescendantId` is the same node or nested under `folderId`. */
export function handbookTrainingIsUnderFolder(
  nodes: HandbookTrainingNode[],
  folderId: string,
  maybeDescendantId: string,
): boolean {
  if (folderId === maybeDescendantId) return true;
  let cur = nodes.find(n => n.id === maybeDescendantId);
  while (cur?.parentId) {
    if (cur.parentId === folderId) return true;
    cur = nodes.find(n => n.id === cur!.parentId);
  }
  return false;
}

export function validateHandbookTrainingMove(
  node: HandbookTrainingNode,
  newParentId: string | null,
  allNodes: HandbookTrainingNode[],
): string | null {
  if ((node.parentId || null) === (newParentId || null)) return null;
  if (newParentId) {
    const parent = allNodes.find(n => n.id === newParentId);
    if (!parent || parent.kind !== 'folder') return 'Choose a valid folder or top level.';
    if (node.kind === 'folder') {
      if (newParentId === node.id) return 'A folder cannot be moved into itself.';
      if (handbookTrainingIsUnderFolder(allNodes, node.id, newParentId)) {
        return 'A folder cannot be moved into one of its subfolders.';
      }
    }
  }
  return null;
}

export type HandbookTrainingParentOption = { value: string | null; label: string };

/** Folder targets for reparenting (excludes self + descendants when editing a folder). */
export function handbookTrainingParentFolderOptions(
  nodes: HandbookTrainingNode[],
  excludeFolderId?: string,
): HandbookTrainingParentOption[] {
  const tree = buildHandbookTrainingTree(nodes, { publishedOnly: false });
  const excluded = new Set<string>();
  if (excludeFolderId) {
    excluded.add(excludeFolderId);
    const markDescendants = (id: string) => {
      for (const n of nodes) {
        if (n.parentId === id) {
          excluded.add(n.id);
          if (n.kind === 'folder') markDescendants(n.id);
        }
      }
    };
    markDescendants(excludeFolderId);
  }

  const opts: HandbookTrainingParentOption[] = [{ value: null, label: 'Top level (section)' }];

  const walk = (items: HandbookTrainingTreeNode[], depth: number) => {
    for (const n of items) {
      if (n.kind !== 'folder' || excluded.has(n.id)) continue;
      const prefix = depth > 0 ? `${'— '.repeat(depth)}` : '';
      opts.push({ value: n.id, label: `${prefix}${n.title}` });
      walk(n.children.filter(c => c.kind === 'folder'), depth + 1);
    }
  };
  walk(tree, 0);
  return opts;
}

export function computeSiblingOrderAfterInsert(
  allNodes: HandbookTrainingNode[],
  draggedId: string,
  targetId: string,
  insertBefore: boolean,
): { parentId: string | null; orderedIds: string[] } | null {
  const dragged = allNodes.find(n => n.id === draggedId);
  const target = allNodes.find(n => n.id === targetId);
  if (!dragged || !target || draggedId === targetId) return null;

  const parentId = target.parentId ?? null;
  const siblings = allNodes
    .filter(n => (n.parentId ?? null) === parentId && n.id !== draggedId)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
  const ids = siblings.map(s => s.id);
  let idx = ids.indexOf(targetId);
  if (idx < 0) return null;
  if (!insertBefore) idx += 1;
  ids.splice(idx, 0, draggedId);
  return { parentId, orderedIds: ids };
}

export function handbookTrainingVideosUnderFolder(
  folder: HandbookTrainingTreeNode,
): HandbookTrainingTreeNode[] {
  if (folder.kind !== 'folder') return [];
  const out: HandbookTrainingTreeNode[] = [];
  const walk = (nodes: HandbookTrainingTreeNode[]) => {
    for (const n of nodes) {
      if (n.kind === 'video') out.push(n);
      else walk(n.children);
    }
  };
  walk(folder.children);
  return out;
}
